import { verifyLaunchConfig } from "../api/launchpad-config";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { QueryClient } from "@tanstack/react-query";
import { MiningSessionApi } from "../api/mining-session";
import type { MiningAllocationPolicy } from "../mining-engine/server-lease";
import { connectedSourceChainId } from "../config/chains";
import { miningAllocationPolicy } from "../config/mining-session";
import { createMiningController } from "../mining-engine";
import { WalletSessionController } from "../wallet/wallet-session";
import { WalletConnection } from "../wallet/wallet-connection";
import { MiningAudio } from "../audio/mining-audio";
import { ScopedReadCache, TasksReadCache } from "./tasks-read-cache";
import type { TaskMintSupply } from "../features/social-tasks/contracts/task-mint-supply";
import { TradeQuoteCache } from "../wallet/trade-quote-cache";
import { BridgeHistoryRegistry } from "../features/bridge/history/controller";
import { MetadataPublisher } from "../features/launchpad/state/metadata-publisher";

export function createLiveRuntime(allocationPolicy: MiningAllocationPolicy | null = miningAllocationPolicy()) {
  const launchConfigAbort = new AbortController();
  // Bootstrap errors are retained; every write independently revalidates config.
  const launchConfiguration = verifyLaunchConfig(launchConfigAbort.signal).catch((error: unknown) => error);
  const walletController = new WalletSessionController(connectedSourceChainId());
  walletController.start();
  const walletConnection = new WalletConnection(walletController);
  void walletConnection.restore().catch(() => { /* An unavailable remembered wallet can be selected again explicitly. */ });
  const miningApi = new MiningSessionApi(walletController.api);
  const miningController = createMiningController({ api: {
    getChallenge: (id, signal) => miningApi.getChallenge(id,
      { walletAddress: walletController.getSnapshot().account ?? "", chainId: walletController.getSnapshot().chainId ?? 0 }, signal),
    submitProof: async () => { throw new Error("MINING_SERVER_PROVENANCE_REQUIRED"); },
  }, server: { api: miningApi, auth: walletController.getSnapshot, allocationPolicy } });
  const miningAudio = new MiningAudio(miningController);
  const tasksReadCache = new TasksReadCache();
  const taskMintSupplyCache = new ScopedReadCache<TaskMintSupply>();
  const balanceQueryClient = new QueryClient();
  const tradeQuoteCache = new TradeQuoteCache();
  const bridgeHistory = new BridgeHistoryRegistry();
  const metadataPublisher = new MetadataPublisher(walletController);
  // Immediate revisions fence even an account/session A -> B -> A round trip.
  let audioWalletRevision = walletController.getRevision();
  let miningAuthRevision = walletController.getSnapshot().sessionRevision;
  let miningAuthenticated = walletController.getSnapshot().authenticated;
  let tradeWalletIdentity = walletController.getIdentityRevision();
  let tradeWalletChain = walletController.getSnapshot().chainId;
  const unsubscribeAudioWallet = walletController.subscribe(() => {
    const auth = walletController.getSnapshot();
    const fenceMining = auth.sessionRevision !== miningAuthRevision || miningAuthenticated && !auth.authenticated;
    miningAuthRevision = auth.sessionRevision;
    miningAuthenticated = auth.authenticated;
    if (fenceMining) {
      // Runtime ownership outlives the Terminal route. Do not wait for a mounted
      // page effect to stop workers after a cookie/context invalidation.
      // Pause retains checkpoints/candidates and never revokes the cookie.
      miningController.pause("WALLET_CONTEXT_CHANGED").catch(() => {
        console.error("Mining pause cleanup failed after authentication changed.");
      });
    }
    const revision = walletController.getRevision();
    if (revision !== audioWalletRevision) { audioWalletRevision = revision; tasksReadCache.clear(); miningAudio.checkContext(); }
    const identity = walletController.getIdentityRevision(), chain = walletController.getSnapshot().chainId;
    if (identity !== tradeWalletIdentity || chain !== tradeWalletChain) {
      tradeWalletIdentity = identity; tradeWalletChain = chain; tradeQuoteCache.clear();
    }
  });
  let disposal: Promise<void> | null = null;
  return {
    launchConfiguration, walletController, walletConnection, miningController, miningAudio, tasksReadCache, taskMintSupplyCache, balanceQueryClient, tradeQuoteCache, bridgeHistory, metadataPublisher,
    dispose() {
      if (disposal) return disposal;
      launchConfigAbort.abort();
      unsubscribeAudioWallet();
      tasksReadCache.clear();
      taskMintSupplyCache.clear();
      balanceQueryClient.clear();
      tradeQuoteCache.clear();
      bridgeHistory.dispose();
      metadataPublisher.dispose();
      miningAudio.dispose();
      walletConnection.dispose();
      walletController.dispose();
      disposal = miningController.dispose();
      return disposal;
    },
  };
}

type LiveRuntime = ReturnType<typeof createLiveRuntime>;
const RuntimeContext = createContext<LiveRuntime | null>(null);

/** Effect-owned instances avoid constructors with side effects during React render. */
export function LiveRuntimeProvider({ children }: { children: ReactNode }) {
  const [runtime, setRuntime] = useState<LiveRuntime | null>(null);
  useEffect(() => {
    const next = createLiveRuntime();
    setRuntime(next);
    return () => {
      void next.dispose().catch(() => {
        // Keep cleanup failures observable without logging wallet data.
        console.error("Live mining runtime cleanup failed.");
      });
    };
  }, []);
  return runtime ? <RuntimeContext.Provider value={runtime}>{children}</RuntimeContext.Provider> : null;
}

export function useLiveRuntime(): LiveRuntime {
  const runtime = useContext(RuntimeContext);
  if (!runtime) throw new Error("LiveRuntimeProvider is required for the live terminal");
  return runtime;
}
